import React, { useState } from 'react';
import {
  useOverviewMetrics,
  useRevenueChart,
  useSessionsBreakdown,
  useClientMetrics,
  useBookingPatterns,
} from '@theraply/api-client';
import { KPICard } from '../../components/analytics/KPICard';
import { RevenueChart } from '../../components/analytics/RevenueChart';
import { SessionsChart } from '../../components/analytics/SessionsChart';
import { ClientsChart } from '../../components/analytics/ClientsChart';
import { BookingPatterns } from '../../components/analytics/BookingPatterns';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  StarIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

type DateRangePeriod = 'week' | 'month' | 'quarter' | 'year' | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Get date range based on period
 */
function getDateRange(period: DateRangePeriod): DateRange {
  const today = new Date();
  const endDate = today.toISOString().split('T')[0];
  let startDate: Date;

  switch (period) {
    case 'week':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      break;
    case 'month':
      startDate = new Date(today);
      startDate.setMonth(today.getMonth() - 1);
      break;
    case 'quarter':
      startDate = new Date(today);
      startDate.setMonth(today.getMonth() - 3);
      break;
    case 'year':
      startDate = new Date(today);
      startDate.setFullYear(today.getFullYear() - 1);
      break;
    default:
      startDate = new Date(today);
      startDate.setMonth(today.getMonth() - 1);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate,
  };
}

/**
 * Format currency
 */
function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Format rating
 */
function formatRating(rating: number): string {
  return rating.toFixed(1);
}

/**
 * Analytics Dashboard Page
 * Displays comprehensive analytics for therapists
 */
export function AnalyticsDashboard() {
  const [period, setPeriod] = useState<DateRangePeriod>('month');
  const [customRange, setCustomRange] = useState<DateRange | null>(null);

  const dateRange = customRange || getDateRange(period);

  // Fetch analytics data
  const overviewQuery = useOverviewMetrics(dateRange);
  const revenueQuery = useRevenueChart({
    ...dateRange,
    period: period === 'week' ? 'daily' : period === 'year' ? 'monthly' : 'daily',
  });
  const sessionsQuery = useSessionsBreakdown(dateRange);
  const clientsQuery = useClientMetrics(dateRange);
  const patternsQuery = useBookingPatterns();

  const isLoading =
    overviewQuery.isLoading ||
    revenueQuery.isLoading ||
    sessionsQuery.isLoading ||
    clientsQuery.isLoading;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            Track your practice performance and business insights
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="mb-8 bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Date Range:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {(['week', 'month', 'quarter', 'year'] as DateRangePeriod[]).map((p) => (
                <button
                  key={p}
                  onClick={() => {
                    setPeriod(p);
                    setCustomRange(null);
                  }}
                  className={clsx(
                    'px-4 py-2 text-sm font-medium rounded-md transition-colors',
                    period === p && !customRange
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  )}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            label="Total Revenue"
            value={overviewQuery.data?.totalRevenue || 0}
            valueFormatter={formatCurrency}
            trend={overviewQuery.data?.revenueChange}
            trendLabel="vs previous period"
            icon={<CurrencyDollarIcon className="h-6 w-6" />}
            loading={overviewQuery.isLoading}
          />
          <KPICard
            label="Completed Sessions"
            value={overviewQuery.data?.totalSessions || 0}
            trend={overviewQuery.data?.sessionsChange}
            trendLabel="vs previous period"
            icon={<ChartBarIcon className="h-6 w-6" />}
            loading={overviewQuery.isLoading}
          />
          <KPICard
            label="Unique Clients"
            value={overviewQuery.data?.uniqueClients || 0}
            icon={<UserGroupIcon className="h-6 w-6" />}
            loading={overviewQuery.isLoading}
          />
          <KPICard
            label="Average Rating"
            value={overviewQuery.data?.averageRating || 0}
            valueFormatter={formatRating}
            icon={<StarIcon className="h-6 w-6" />}
            loading={overviewQuery.isLoading}
          />
        </div>

        {/* Revenue Chart */}
        <div className="mb-8">
          <RevenueChart
            data={revenueQuery.data?.data || []}
            loading={revenueQuery.isLoading}
          />
        </div>

        {/* Session and Client Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <SessionsChart
            data={
              sessionsQuery.data || {
                byStatus: {
                  COMPLETED: 0,
                  CONFIRMED: 0,
                  CANCELLED: 0,
                  PENDING_PAYMENT: 0,
                },
                byType: { INTAKE: 0, REGULAR: 0, CRISIS: 0 },
                total: 0,
                completionRate: 0,
                cancellationRate: 0,
              }
            }
            loading={sessionsQuery.isLoading}
          />
          <ClientsChart
            data={
              clientsQuery.data || {
                newClients: 0,
                returningClients: 0,
                totalClients: 0,
                retentionRate: 0,
                topClients: [],
              }
            }
            loading={clientsQuery.isLoading}
          />
        </div>

        {/* Booking Patterns */}
        <div className="mb-8">
          <BookingPatterns
            data={
              patternsQuery.data || {
                heatmapData: [],
                byDayOfWeek: {},
                byHourOfDay: {},
                dayOfWeekLabels: [],
                peakHours: [],
              }
            }
            loading={patternsQuery.isLoading}
          />
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Data is updated in real-time. Last updated:{' '}
            {new Date().toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
